import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { PaginatedResult } from 'src/app/app.model';
import { catchError, map, switchMap } from 'rxjs/operators';
import { RequestCategory } from 'src/app/global/services/requests/requests.model';
import { environment } from '../../../environments/environment';
import { Request, RequestFile, DepartmentType, StatusType, RequestSubtask } from '../models/request.model';
import { RequestSubtaskDto, UpdateRequestSubtaskDto } from '../models/request-subtask.model';
import { CreateRequestPayload } from '../models/create-request.dto';
import { FilterOptions } from '../components/filter-menu/filter-menu.component';

@Injectable({
    providedIn: 'root',
})
export class RequestsV2ImplService {
    private baseUrl = `${environment.API_GATEWAY_URL}/request-v2/admin`;

    private readonly addressBaseUrl: string = `${environment.API_GATEWAY_URL}/address`;

    constructor(private http: HttpClient) {}

    getRequestCategories(search?: string): Observable<RequestCategory[]> {
        let params = new HttpParams();

        if (search && search.trim()) {
            params = params.set('search', search.trim());
        }

        return this.http.get(`${this.baseUrl}/request-category`, { params }).pipe(map((response: any) => response));
    }

    postRequest(request: CreateRequestPayload): Observable<Request> {
        return this.http.post<Request>(`${this.baseUrl}/request`, request).pipe(
            catchError((error) => {
                console.error('Error creating request:', error);
                throw error;
            }),
        );
    }

    getRequests(filters?: FilterOptions, page = 1, pageSize = 10): Observable<PaginatedResult<Request>> {
        let params = new HttpParams().set('page', page.toString()).set('limit', pageSize.toString());

        if (filters) {
            // Handle date fields separately first
            if (filters.createdAtFrom) {
                const date = new Date(filters.createdAtFrom);
                if (!isNaN(date.getTime())) {
                    params = params.set('createdAtFrom', date.toISOString());
                }
            }

            if (filters.createdAtTo) {
                const date = new Date(filters.createdAtTo);
                if (!isNaN(date.getTime())) {
                    params = params.set('createdAtTo', date.toISOString());
                }
            }

            if (filters.slaDate) {
                const date = new Date(filters.slaDate);
                if (!isNaN(date.getTime())) {
                    params = params.set('slaDate', date.toISOString());
                }
            }

            // Handle other filter fields
            Object.entries(filters).forEach(([key, value]) => {
                // Skip date fields (already handled above) and special fields (handled below)
                if (
                    key === 'createdAtFrom' ||
                    key === 'createdAtTo' ||
                    key === 'slaDate' ||
                    key === 'search' ||
                    key === 'page' ||
                    key === 'limit' ||
                    key === 'sortBy' ||
                    key === 'sortOrder' ||
                    key === 'tagsFilterMode'
                ) {
                    return;
                }

                if (key === 'department' && value === 'unassigned') {
                    params = params.set('department', null);
                } else if (value !== null && value !== undefined && value !== '') {
                    if (typeof value === 'boolean') {
                        params = params.set(key, value.toString());
                    } else if (typeof value === 'string' || typeof value === 'number') {
                        params = params.set(key, value);
                    }
                }
            });

            if (filters.tagsFilterMode) {
                params = params.set('tagsFilterMode', filters.tagsFilterMode);
            }

            if (filters.search) {
                params = params.set('search', filters.search);
            }

            if (filters.sortBy) {
                params = params.set('sortBy', filters.sortBy);
            }

            if (filters.sortOrder) {
                params = params.set('sortOrder', filters.sortOrder);
            }
        }

        return this.http.get(`${this.baseUrl}/request`, { params }).pipe(map((response) => response as PaginatedResult<Request>));
    }

    getOrgTags(): Observable<string[]> {
        return this.http.get<string[]>(`${this.baseUrl}/request-tag`);
    }

    searchTags(search: string): Observable<string[]> {
        const params = new HttpParams().set('search', search);
        return this.http.get<string[]>(`${this.baseUrl}/request-tag`, { params });
    }

    updateRequestTags(requestId: string, tags: string[]): Observable<Request> {
        return this.http.patch<Request>(`${this.baseUrl}/request/${requestId}/tags`, { tags });
    }

    getRequestDetails(id: string): Observable<Request> {
        return this.http.get(`${this.baseUrl}/request/${id}`).pipe(map((response) => response as Request));
    }

    updateRequestStatus(requestId: string, statusId: string): Observable<Request> {
        return this.http.patch<Request>(`${this.baseUrl}/request/${requestId}/status`, { status: statusId });
    }

    updateRequestDepartment(requestId: string, departmentId: string | null): Observable<Request> {
        return this.http.patch<Request>(`${this.baseUrl}/request/${requestId}/department`, { department: departmentId });
    }

    updateRequestSubCategory(requestId: string, subCategoryId: string): Observable<Request> {
        return this.http.patch<Request>(`${this.baseUrl}/request/${requestId}`, { subCategory: subCategoryId });
    }

    updateDescription(requestId: string, description: string): Observable<Request> {
        return this.http.patch<Request>(`${this.baseUrl}/request/${requestId}`, { description });
    }

    updateInternalDescription(requestId: string, internalDescription: string): Observable<Request> {
        return this.http.patch<Request>(`${this.baseUrl}/request/${requestId}`, { internalDescription });
    }

    updateRequestCategoryAndSubject(requestId: string, categoryId: string, subCategoryId: string): Observable<Request> {
        return this.http.patch<Request>(`${this.baseUrl}/request/${requestId}/category-and-subject`, {
            category: categoryId,
            subCategory: subCategoryId,
        });
    }

    updateRequestSubtask(requestId: string, subtaskId: string, subtask: UpdateRequestSubtaskDto): Observable<RequestSubtaskDto> {
        return this.http.patch<RequestSubtaskDto>(`${this.baseUrl}/request/${requestId}/subtask/${subtaskId}`, subtask);
    }

    updateRequestSubtasksList(requestId: string, subtasks: UpdateRequestSubtaskDto[]): Observable<RequestSubtaskDto[]> {
        return this.http.patch<RequestSubtaskDto[]>(`${this.baseUrl}/request/${requestId}/subtask`, { subtasks });
    }

    createRequestInternalNote(requestId: string, description: string): Observable<Request> {
        return this.http.post<any>(`${this.baseUrl}/request/${requestId}/internal-notes`, { description }).pipe(switchMap(() => this.getRequestDetails(requestId)));
    }

    editInternalNote(requestId: string, noteId: string, description: string): Observable<Request> {
        return this.http.patch<void>(`${this.baseUrl}/request/${requestId}/internal-notes/${noteId}`, { description }).pipe(switchMap(() => this.getRequestDetails(requestId)));
    }

    getDepartments(): Observable<DepartmentType[]> {
        return this.http.get<DepartmentType[]>(`${this.baseUrl}/request-department`);
    }

    getAllStatusesByOrg(): Observable<StatusType[]> {
        const url = `${this.baseUrl}/request-category/status`;

        return this.http.get<StatusType[]>(url).pipe(map((response) => response as StatusType[]));
    }

    deleteRequestSubtask(requestId: string, subtaskId: string): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(`${this.baseUrl}/request/${requestId}/subtask/${subtaskId}`);
    }

    assignRequestSubtask(requestId: string, subtaskId: string, assignedTo: string | null): Observable<RequestSubtask> {
        return this.http.patch<RequestSubtask>(`${this.baseUrl}/request/${requestId}/subtask/${subtaskId}/assign`, { assignedTo });
    }

    setRequestSubtaskCompletion(requestId: string, subtaskId: string, isDone: boolean, completedAt?: string): Observable<RequestSubtaskDto> {
        const body: UpdateRequestSubtaskDto = { isDone };

        if (completedAt) {
            body.completedAt = completedAt;
        }

        return this.http.patch<RequestSubtaskDto>(`${this.baseUrl}/request/${requestId}/subtask/${subtaskId}`, body);
    }

    getAddressFromCoordinates(latitude: number, longitude: number): Observable<any> {
        if (latitude == null || longitude == null) {
            return throwError('Latitude and Longitude are required');
        }

        const params = new HttpParams().set('lat', latitude.toString()).set('lng', longitude.toString());

        return this.http.get<any>(`${this.addressBaseUrl}/geopos`, { params }).pipe(catchError((error) => throwError(error)));
    }

    uploadFile(file: File): Observable<string[]> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<string[]>(`${environment.API_GATEWAY_URL}/requests/fileupload/temp`, formData).pipe(
            map((response: any) => response as string[]),
            catchError((error) => throwError(error)),
        );
    }

    attachAdditionalFile(requestId: string, files: RequestFile[]): Observable<Request> {
        return this.http.post<Request>(`${this.baseUrl}/request/${requestId}/files`, { files });
    }

    deleteFile(requestId: string, index: number): Observable<Request> {
        return this.http.delete<Request>(`${this.baseUrl}/request/${requestId}/files/${index}`);
    }

    attachInternalFile(requestId: string, internalFiles: RequestFile[]): Observable<Request> {
        return this.http.post<Request>(`${this.baseUrl}/request/${requestId}/internal-files`, { internalFiles }).pipe(switchMap(() => this.getRequestDetails(requestId)));
    }

    deleteInternalFile(requestId: string, index: number): Observable<Request> {
        return this.http.delete<Request>(`${this.baseUrl}/request/${requestId}/internal-files/${index}`).pipe(switchMap(() => this.getRequestDetails(requestId)));
    }

    linkRequest(requestId: string, link: { type: string; linkedRequestId: string }): Observable<Request> {
        return this.http.post<Request>(`${this.baseUrl}/request/${requestId}/link`, link).pipe(switchMap(() => this.getRequestDetails(requestId)));
    }

    updateLinkRequest(requestId: string, link: { type: string; linkedRequestId: string }): Observable<Request> {
        return this.http.patch<Request>(`${this.baseUrl}/request/${requestId}/link`, link).pipe(switchMap(() => this.getRequestDetails(requestId)));
    }

    deleteLinkRequest(requestId: string, linkedRequestId: string): Observable<Request> {
        return this.http.delete<Request>(`${this.baseUrl}/request/${requestId}/link/${linkedRequestId}`).pipe(switchMap(() => this.getRequestDetails(requestId)));
    }

    // Send a message to a request
    sendMessage(requestId: string, message: string, attachments?: string[]): Observable<Request> {
        // API requires attachments array - default to [''] when no attachments
        const body = {
            message,
            attachments: attachments && attachments.length > 0 ? attachments : [''],
        };
        return this.http.post<Request>(`${this.baseUrl}/request/${requestId}/message`, body).pipe(switchMap(() => this.getRequestDetails(requestId)));
    }

    // Mark messages as read by admin
    markMessagesAsRead(requestId: string): Observable<Request> {
        return this.http.get<Request>(`${this.baseUrl}/request/${requestId}/message/read`);
    }
}
